import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, FileSearch, Copy, X } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

const fmt = (n) =>
  n == null ? '' : Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const toIsoDate = (d) => d.toISOString().slice(0, 10);
const isRealizationDocument = (name = '') =>
  name.toLowerCase().includes('реализация товаров и услуг');

/* ── Invoice modal ───────────────────────────────────────────────────────── */
const InvoiceModal = ({ html, loading, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="relative bg-white rounded-xl shadow-2xl w-[min(97vw,1600px)] max-h-[94vh] flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
        <span className="font-semibold text-gray-800 text-sm">Накладная</span>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="overflow-auto flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  </div>
);

/* ── Page ────────────────────────────────────────────────────────────────── */
const ReconciliationPage = () => {
  const { token } = useAuth();

  const today   = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  const [dateFrom, setDateFrom] = useState(toIsoDate(weekAgo));
  const [dateTo, setDateTo]     = useState(toIsoDate(today));
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);

  const [invoiceHtml, setInvoiceHtml]       = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceOpen, setInvoiceOpen]       = useState(false);
  const [simplifiedCopyingDid, setSimplifiedCopyingDid] = useState('');
  const didAutoFetch = useRef(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAct = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/interkom/act`, {
        params: { date_from: dateFrom, date_to: dateTo },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка загрузки акта сверки');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, token]);

  const fetchCachedAct = useCallback(async () => {
    if (!token) return false;
    try {
      const { data } = await axios.get(`${API}/interkom/act/cache`, {
        params: { date_from: dateFrom, date_to: dateTo },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(data?.rows) && data.rows.length > 0) {
        setResult(data);
        return true;
      }
    } catch {
      // Cache is only an optimistic first paint; the live Interkom request follows.
    }
    return false;
  }, [dateFrom, dateTo, token]);

  useEffect(() => {
    if (!token || didAutoFetch.current) return;
    didAutoFetch.current = true;
    const loadInitialAct = async () => {
      await fetchCachedAct();
      await fetchAct();
    };
    loadInitialAct();
  }, [fetchAct, fetchCachedAct, token]);

  const openInvoice = async (did) => {
    if (!did) return;
    setInvoiceHtml('');
    setInvoiceLoading(true);
    setInvoiceOpen(true);
    try {
      const { data } = await axios.get(`${API}/interkom/invoice`, {
        params: { did },
        headers: authHeaders,
      });
      setInvoiceHtml(data);
    } catch (err) {
      setInvoiceOpen(false);
      toast.error(err.response?.data?.detail || 'Ошибка загрузки накладной');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const fallbackTextarea = document.createElement('textarea');
      fallbackTextarea.value = text;
      fallbackTextarea.setAttribute('readonly', '');
      fallbackTextarea.style.position = 'fixed';
      fallbackTextarea.style.opacity = '0';
      document.body.appendChild(fallbackTextarea);
      fallbackTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(fallbackTextarea);
    }
  };

  const copySimplifiedInvoice = async (did) => {
    if (!did || simplifiedCopyingDid) return;
    setSimplifiedCopyingDid(did);
    try {
      const { data } = await axios.get(`${API}/interkom/invoice/simplified`, {
        params: { did },
        headers: authHeaders,
      });
      const text = data?.clipboard_text || '';
      if (!text.trim()) {
        toast.warning('В документе нет строк для копирования');
        return;
      }
      await copyTextToClipboard(text);
      toast.success('Данные для 1С скопированы');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка загрузки упрощённого документа');
    } finally {
      setSimplifiedCopyingDid('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Invoice modal */}
      {invoiceOpen && (
        <InvoiceModal
          html={invoiceHtml}
          loading={invoiceLoading}
          onClose={() => setInvoiceOpen(false)}
        />
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/')}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Назад
          </Button>
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
            <FileText className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Акт сверки</h1>
            <p className="text-xs text-gray-400">Данные из личного кабинета Интерком</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-6xl mx-auto space-y-4">
        {/* Date filter */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <div className="flex flex-wrap items-end gap-5">
            <div>
              <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wide">
                Дата начала периода
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wide">
                Дата окончания периода
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <Button onClick={fetchAct} disabled={loading} className="h-9">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Загрузка…
                </>
              ) : (
                'Запросить'
              )}
            </Button>
          </div>
        </div>

        {/* Table */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Balance */}
            {result.balance != null && (
              <div className={`px-5 py-3 border-b border-gray-100 flex items-center justify-between text-sm font-semibold ${
                result.balance < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                <span>Текущий баланс</span>
                <span className="text-base tabular-nums">{fmt(result.balance)}&nbsp;₸</span>
              </div>
            )}

            {result.rows.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">
                За указанный период данных нет
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                      <th className="px-4 py-3 text-left" rowSpan={2}>Документ</th>
                      <th className="px-4 py-3 text-right border-l border-gray-200" rowSpan={2}>
                        Начальный<br />остаток
                      </th>
                      <th className="px-4 py-3 text-center border-l border-gray-200" colSpan={2}>
                        Долг клиента
                      </th>
                      <th className="px-4 py-3 text-right border-l border-gray-200" rowSpan={2}>Наш долг</th>
                      <th className="px-4 py-3 text-right border-l border-gray-200" rowSpan={2}>
                        Долг<br />клиента
                      </th>
                    </tr>
                    <tr className="bg-gray-50 border-b-2 border-gray-200 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                      <th className="px-4 py-2 text-right border-l border-gray-200">Увеличение</th>
                      <th className="px-4 py-2 text-right border-l border-gray-100">Уменьшение</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.rows.map((row, idx) => {
                      const red   = row.is_red;
                      const total = row.is_total;
                      const numCls = red
                        ? 'text-red-600'
                        : total
                        ? 'text-gray-900 font-bold'
                        : 'text-gray-800';

                      return (
                        <tr
                          key={idx}
                          className={`transition-colors ${total ? 'bg-gray-50' : 'hover:bg-blue-50/30'}`}
                        >
                          {/* Document */}
                          <td className={`px-4 py-2.5 ${red ? 'text-red-700' : total ? 'text-gray-700 font-semibold' : 'text-gray-800'}`}>
                            <div className="flex items-center gap-2">
                              {row.did ? (
                                <button
                                  onClick={() => openInvoice(row.did)}
                                  className={`text-left hover:underline ${red ? 'text-red-600' : 'text-blue-600'}`}
                                >
                                  {row.document}
                                </button>
                              ) : (
                                <span>{row.document}</span>
                              )}
                              {row.did && (
                                <button
                                  onClick={() => openInvoice(row.did)}
                                  title="Открыть накладную"
                                  className="text-gray-400 hover:text-blue-500 flex-shrink-0"
                                >
                                  <FileSearch className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {row.did && isRealizationDocument(row.document || '') && (
                                <button
                                  onClick={() => copySimplifiedInvoice(row.did)}
                                  title="Скопировать для 1С"
                                  disabled={simplifiedCopyingDid === row.did}
                                  className="text-gray-400 hover:text-emerald-600 disabled:text-emerald-500 disabled:cursor-wait flex-shrink-0"
                                >
                                  {simplifiedCopyingDid === row.did ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>

                          <td className={`px-4 py-2.5 text-right border-l border-gray-100 tabular-nums ${numCls}`}>
                            {fmt(row.opening_balance)}
                          </td>
                          <td className={`px-4 py-2.5 text-right border-l border-gray-100 tabular-nums ${numCls}`}>
                            {fmt(row.increase)}
                          </td>
                          <td className={`px-4 py-2.5 text-right border-l border-gray-100 tabular-nums ${numCls}`}>
                            {fmt(row.decrease)}
                          </td>
                          <td className={`px-4 py-2.5 text-right border-l border-gray-100 tabular-nums ${numCls}`}>
                            {fmt(row.our_debt)}
                          </td>
                          <td className={`px-4 py-2.5 text-right border-l border-gray-100 tabular-nums ${numCls}`}>
                            {fmt(row.client_debt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {result.fetched_at && (
              <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 text-right">
                Данные получены:{' '}
                {new Date(result.fetched_at).toLocaleString('ru-RU', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconciliationPage;
